import { Check, DoneAll, Notes, School } from '@mui/icons-material';
import { Alert, Box, Card, Chip, Divider, LinearProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import PlannedFinishDate from '@/components/course/planned-finish-date';
import CircularProgressLabel from '@/components/shared/circular-progress-label';
import TimeDifference from '@/components/shared/time-difference';
import { useCourses } from '@/providers';

const sxRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 1,
};

const sxValue = {
  display: 'flex',
  fontWeight: 700,
  textAlign: 'right',
};

export default function CourseStats({ courseId, testAt }: { courseId: string; testAt?: string }) {
  const { isLoading, courseStatus } = useCourses();

  if (isLoading || !courseStatus) return <LinearProgress />;
  const { chapters, exercises, units, plannedToFinishAt } = courseStatus;
  const daysToPlannedFinish = dayjs(plannedToFinishAt).diff(dayjs(), 'days');
  const chaptersPerDay =
    daysToPlannedFinish > 0
      ? Math.round((chapters.total - chapters.completed) / daysToPlannedFinish)
      : chapters.total - chapters.completed;
  const correctProgress =
    exercises.total > 0 ? Math.round((exercises.correct / exercises.total) * 100) : 0;

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, userSelect: 'none' }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Dein Lernplan
      </Typography>
      {!plannedToFinishAt && (
        <Alert variant="outlined" severity="warning" sx={{ mb: 2 }}>
          Passe Dein Lernende Deinen Bedürfnissen an.
        </Alert>
      )}
      <Alert variant="outlined" severity="info" sx={{ mb: 2 }}>
        Hier kannst du Dein individuelles Lernende festlegen. Dies ist der Tag, an dem Du die
        letzten neuen Inhalte lernst. Idealerweise liegt dieser 2-3 Wochen vor Deinem Test.
      </Alert>
      {testAt && (
        <Box sx={sxRow}>
          Zum Test ({dayjs(testAt).format('DD.MM.YYYY')}):
          <Box component="span" sx={sxValue}>
            <TimeDifference to={testAt} /> Tage
          </Box>
        </Box>
      )}
      <Box sx={sxRow}>
        Dein Lernende:
        <PlannedFinishDate courseId={courseId} date={plannedToFinishAt} testAt={testAt} />
      </Box>
      <Box sx={sxRow}>
        Zum festgelegten Lernende:
        {plannedToFinishAt && (
          <Box component="span" sx={sxValue}>
            <TimeDifference to={plannedToFinishAt} /> Tage
          </Box>
        )}
      </Box>
      <Box sx={sxRow}>
        <Typography>Pensum zum Lernende:</Typography>
        {chaptersPerDay === 0 && chapters.progress === 100 ? (
          <Chip label="🎉 Abgeschlossen" size="small" variant="outlined" color="success" />
        ) : (
          <Box component="span" sx={sxValue}>
            <strong>{chaptersPerDay} Kapitel / Tag</strong>
          </Box>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={sxRow}>
        <strong>Gesamtfortschritt ({chapters.total} Kapitel):</strong>
        <CircularProgressLabel value={chapters.progress} />
      </Box>
      {exercises.total > 0 && (
        <Box sx={sxRow}>
          <Typography>
            <School fontSize="small" sx={{ verticalAlign: 'text-bottom', mr: 0.5 }} />
            Ausgewertete Übungen
            <Check fontSize="small" sx={{ verticalAlign: 'text-bottom', mx: 0.5 }} />(
            {exercises.completed}/{exercises.total}
            ):
          </Typography>
          <CircularProgressLabel value={exercises.progress} />
        </Box>
      )}
      {exercises.total > 0 && (
        <Box sx={sxRow}>
          <Typography>
            <School fontSize="small" sx={{ verticalAlign: 'text-bottom', mr: 0.5 }} />
            Ausgewertete Übungen vollständig richtig
            <DoneAll
              fontSize="small"
              color="success"
              sx={{ verticalAlign: 'text-bottom', mx: 0.5 }}
            />
            ({exercises.correct}/{exercises.total}
            ):
          </Typography>
          <CircularProgressLabel value={correctProgress} />
        </Box>
      )}
      {units.total > 0 && (
        <Box sx={sxRow}>
          <Typography>
            <Notes fontSize="small" sx={{ verticalAlign: 'text-bottom', mr: 0.5 }} />
            Abgeschlossene Lektionen
            <Check fontSize="small" sx={{ verticalAlign: 'text-bottom', mx: 0.5 }} />(
            {units.completed}/{units.total}):
          </Typography>
          <CircularProgressLabel value={units.progress} />
        </Box>
      )}
    </Card>
  );
}
